/**
 * Field List Table Component
 * Displays fields with irrigation monitoring data
 * Supports Advanced Mode toggle for additional columns
 */

const React = require('react');
const { useState, useMemo } = React;
const { useTranslation } = require('react-i18next');
const PropTypes = require('prop-types');
const IrrigationActivityIndicator = require('./IrrigationActivityIndicator.jsx');
const WeeklyPerformanceBar = require('./WeeklyPerformanceBar.jsx');
const StressGauge = require('./StressGauge.jsx');
const RootZoneWP = require('./RootZoneWP.jsx');
const RecommendationDisplay = require('./RecommendationDisplay.jsx');
require('./FieldListTable.css');

/**
 * Truncates text with ellipsis in the middle if it exceeds maxLength
 * @param {string} text - The text to truncate
 * @param {number} maxLength - Maximum character length before truncating
 * @returns {string} Truncated text with middle ellipsis or original text
 */
const truncateMiddle = (text, maxLength = 60) => {
  if (!text || text.length <= maxLength) return text;

  const ellipsis = '...';
  const charsToShow = maxLength - ellipsis.length;
  const frontChars = Math.ceil(charsToShow / 2);
  const backChars = Math.floor(charsToShow / 2);

  return text.slice(0, frontChars) + ellipsis + text.slice(-backChars);
};

/**
 * FieldListTable component
 * @param {Object} props
 * @param {Array} props.fields - Array of field data
 * @param {boolean} props.isAdvancedMode - Show advanced columns
 * @param {Function} props.onAdvancedModeToggle - Callback for toggle
 * @param {Function} props.onFieldSelect - Callback when field is selected
 * @param {string} props.selectedFieldId - Currently selected field ID
 */
const FieldListTable = ({
  fields,
  isAdvancedMode,
  onAdvancedModeToggle,
  onFieldSelect,
  selectedFieldId,
}) => {
  const { t } = useTranslation();
  const [sortConfig, setSortConfig] = useState({
    key: 'name',
    direction: 'asc',
  });

  /**
   * Sort fields based on current sort config
   */
  const sortedFields = useMemo(() => {
    if (!fields || fields.length === 0) return [];

    const sorted = [...fields].sort((a, b) => {
      let aValue, bValue;

      switch (sortConfig.key) {
        case 'name':
          aValue = a.name || '';
          bValue = b.name || '';
          break;
        case 'stress':
          aValue = a.stressGauge?.value || 0;
          bValue = b.stressGauge?.value || 0;
          break;
        case 'irrigation':
          aValue = a.irrigationActivity?.irrigationEndDate || 0;
          bValue = b.irrigationActivity?.irrigationEndDate || 0;
          break;
        case 'performance':
          aValue = a.weeklyPerformance?.percentage || 0;
          bValue = b.weeklyPerformance?.percentage || 0;
          break;
        default:
          return 0;
      }

      if (aValue < bValue) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });

    return sorted;
  }, [fields, sortConfig]);

  /**
   * Handle column header click for sorting
   */
  const handleSort = (key) => {
    const direction =
      sortConfig.key === key && sortConfig.direction === 'asc' ? 'desc' : 'asc';
    setSortConfig({ key, direction });
  };

  /**
   * Handle field row click
   */
  const handleFieldClick = (field) => {
    if (onFieldSelect) {
      onFieldSelect(field);
    }
  };

  /**
   * Handle advanced mode toggle
   */
  const handleAdvancedModeToggle = () => {
    if (onAdvancedModeToggle) {
      onAdvancedModeToggle();
    }
  };

  /**
   * Get water availability badge label
   */
  const getWaterAvailabilityLabel = (level) => {
    switch (level?.toLowerCase()) {
      case 'low':
        return t('water_availability_low');
      case 'medium':
        return t('water_availability_medium');
      case 'high':
        return t('water_availability_high');
      default:
        return '';
    }
  };

  if (!fields || fields.length === 0) {
    return (
      <div
        className={`field-list-table ${!isAdvancedMode ? 'field-list-table--basic' : ''} field-list-table--empty`}
      >
        <span
          className='text-style-running-16-regular'
          style={{ color: 'var(--color-grey-71)' }}
        >
          {t('no_fields_available')}
        </span>
      </div>
    );
  }

  // Basic mode (sidebar closed) - simplified view
  if (!isAdvancedMode) {
    return (
      <div className='field-list-table field-list-table--basic'>
        {/* Action Bar with Advanced Button */}
        <div className='field-list-table__action-bar'>
          <button
            className='field-list-table__advanced-btn'
            onClick={handleAdvancedModeToggle}
          >
            {t('advanced')}
          </button>
        </div>

        {/* Table Container */}
        <div className='field-list-table__container'>
          <table className='field-list-table__table'>
            <thead>
              <tr>
                <th
                  className='field-list-table__th field-list-table__th--sortable'
                  onClick={() => handleSort('name')}
                >
                  <span
                    className='text-style-short-lables-12-semi-bold'
                    style={{ color: '#6B7A99' }}
                  >
                    {t('field_name')}
                    {sortConfig.key === 'name' && (
                      <span className='field-list-table__sort-indicator'>
                        {sortConfig.direction === 'asc' ? ' ↑' : ' ↓'}
                      </span>
                    )}
                  </span>
                </th>
                <th className='field-list-table__th'>
                  <span
                    className='text-style-short-lables-12-semi-bold'
                    style={{ color: '#6B7A99' }}
                  >
                    {t('crop_water_availability')}
                  </span>
                </th>
              </tr>
            </thead>
            <tbody>
              {sortedFields.map((field) => (
                <tr
                  key={field.id}
                  className={`field-list-table__row ${
                    selectedFieldId === field.id
                      ? 'field-list-table__row--selected'
                      : ''
                  }`}
                  onClick={() => handleFieldClick(field)}
                >
                  <td className='field-list-table__td'>
                    <span
                      className='text-style-short-lables-14-semi-bold field-list-table__field-name'
                      style={{ color: '#2A2F40' }}
                      title={field.name}
                    >
                      {truncateMiddle(field.name, 45)}
                    </span>
                  </td>
                  <td className='field-list-table__td'>
                    {field.waterAvailabilityLevel &&
                      field.waterAvailabilityLevel !== 'none' && (
                        <span
                          className={`field-list-table__water-badge field-list-table__water-badge--${field.waterAvailabilityLevel}`}
                        >
                          {getWaterAvailabilityLabel(
                            field.waterAvailabilityLevel,
                          )}
                        </span>
                      )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  // Advanced mode (sidebar open) - full view
  return (
    <div className='field-list-table'>
      {/* Header with Advanced Mode toggle */}
      <div className='field-list-table__header'>
        <span
          className='text-style-short-lables-16-semi-bold'
          style={{ color: 'var(--color-grey-34)' }}
        >
          {t('fields')}
        </span>
        <label className='field-list-table__toggle'>
          <input
            type='checkbox'
            checked={isAdvancedMode}
            onChange={handleAdvancedModeToggle}
          />
          <span className='field-list-table__toggle-slider' />
          <span
            className='text-style-short-lables-14-regular'
            style={{ color: 'var(--color-grey-45)' }}
          >
            {t('advanced_mode')}
          </span>
        </label>
      </div>

      {/* Table */}
      <div className='field-list-table__container'>
        <table className='field-list-table__table'>
          <thead>
            <tr>
              <th
                className='field-list-table__th field-list-table__th--sortable'
                onClick={() => handleSort('name')}
              >
                <span className='text-style-short-lables-12-semi-bold'>
                  {t('field_name')}
                  {sortConfig.key === 'name' && (
                    <span className='field-list-table__sort-indicator'>
                      {sortConfig.direction === 'asc' ? ' ↑' : ' ↓'}
                    </span>
                  )}
                </span>
              </th>
              <th
                className='field-list-table__th field-list-table__th--sortable'
                onClick={() => handleSort('irrigation')}
              >
                <span className='text-style-short-lables-12-semi-bold'>
                  {t('irrigation_activity')}
                  {sortConfig.key === 'irrigation' && (
                    <span className='field-list-table__sort-indicator'>
                      {sortConfig.direction === 'asc' ? ' ↑' : ' ↓'}
                    </span>
                  )}
                </span>
              </th>
              <th
                className='field-list-table__th field-list-table__th--sortable'
                onClick={() => handleSort('performance')}
              >
                <span className='text-style-short-lables-12-semi-bold'>
                  {t('weekly_performance')}
                  {sortConfig.key === 'performance' && (
                    <span className='field-list-table__sort-indicator'>
                      {sortConfig.direction === 'asc' ? ' ↑' : ' ↓'}
                    </span>
                  )}
                </span>
              </th>
              <th
                className='field-list-table__th field-list-table__th--sortable'
                onClick={() => handleSort('stress')}
              >
                <span className='text-style-short-lables-12-semi-bold'>
                  {t('stress_level')}
                  {sortConfig.key === 'stress' && (
                    <span className='field-list-table__sort-indicator'>
                      {sortConfig.direction === 'asc' ? ' ↑' : ' ↓'}
                    </span>
                  )}
                </span>
              </th>
              <th className='field-list-table__th'>
                <span className='text-style-short-lables-12-semi-bold'>
                  {t('root_zone_wp')}
                </span>
              </th>
              <th className='field-list-table__th'>
                <span className='text-style-short-lables-12-semi-bold'>
                  {t('recommendation')}
                </span>
              </th>
            </tr>
          </thead>
          <tbody>
            {sortedFields.map((field) => (
              <tr
                key={field.id}
                className={`field-list-table__row ${
                  selectedFieldId === field.id
                    ? 'field-list-table__row--selected'
                    : ''
                }`}
                onClick={() => handleFieldClick(field)}
              >
                <td className='field-list-table__td'>
                  <span
                    className='text-style-short-lables-14-semi-bold field-list-table__field-name'
                    style={{ color: 'var(--color-grey-34)' }}
                    title={field.name}
                  >
                    {truncateMiddle(field.name, 45)}
                  </span>
                </td>
                <td className='field-list-table__td'>
                  <IrrigationActivityIndicator
                    isIrrigating={
                      field.irrigationActivity?.isIrrigating || false
                    }
                    displayText={field.irrigationActivity?.displayText || 'N/A'}
                    source={field.irrigationActivity?.source}
                  />
                </td>
                <td className='field-list-table__td'>
                  <WeeklyPerformanceBar
                    actualValue={field.weeklyPerformance?.actualValue || 0}
                    plannedValue={field.weeklyPerformance?.plannedValue || 0}
                    totalBarWidth={field.weeklyPerformance?.totalBarWidth || 75}
                    percentage={field.weeklyPerformance?.percentage || 0}
                    plannedPosition={
                      field.weeklyPerformance?.plannedPosition || 75
                    }
                    unit={field.weeklyPerformance?.unit || 'mm'}
                  />
                </td>
                <td className='field-list-table__td'>
                  <StressGauge
                    level={field.stressGauge?.level || 'None'}
                    value={field.stressGauge?.value || 0}
                    fieldName={field.name}
                  />
                </td>
                <td className='field-list-table__td'>
                  <RootZoneWP
                    value={field.rootZoneWP?.value}
                    startDate={field.rootZoneWP?.startDate}
                    unit={field.rootZoneWP?.unit || 'kPa'}
                  />
                </td>
                <td className='field-list-table__td'>
                  <RecommendationDisplay
                    amount={field.recommendation?.amount}
                    unit={field.recommendation?.unit || 'mm'}
                    frequencyDays={field.recommendation?.frequencyDays}
                    timesPerWeek={field.recommendation?.timesPerWeek}
                    displayText={field.recommendation?.displayText}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

FieldListTable.propTypes = {
  fields: PropTypes.arrayOf(PropTypes.object).isRequired,
  isAdvancedMode: PropTypes.bool.isRequired,
  onAdvancedModeToggle: PropTypes.func.isRequired,
  onFieldSelect: PropTypes.func,
  selectedFieldId: PropTypes.string,
};

FieldListTable.defaultProps = {
  onFieldSelect: null,
  selectedFieldId: null,
};

module.exports = FieldListTable;
